'use client';

import { invariant } from '@ifixit/helpers';
import { cn } from '@ifixit/ui/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import {
   type ComponentProps,
   type KeyboardEvent,
   type MouseEvent,
   type PointerEvent,
   type ReactNode,
   type RefObject,
   createContext,
   use,
   useCallback,
   useId,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { useControllableState } from '../hooks/use-controllable-state';
import { useStableCallback } from '../hooks/use-stable-callback';
import { afterExit } from '../lib/after-exit';
import { assignRef } from '../lib/assign-ref';
import { type PopupPlacement, followAnchor, placePopup } from '../lib/place-popup';
import { FaIcon } from './icon';

/**
 * A menu button (WAI-ARIA APG). Opening moves focus into the menu, the arrow
 * keys move it from item to item, and closing hands it back to the trigger.
 * The menu renders in place and is lifted into the top layer with the Popover
 * API, which keeps it inside whatever dialog, drawer or sheet owns the trigger
 * while escaping their clipping and transforms.
 */

/** Where focus lands on opening: the menu itself after a pointer press, so no
 * item lights up under the pointer, or the first or last item from a key. */
type OpenFocus = 'menu' | 'first' | 'last';

interface DropdownMenuContextValue {
   open: boolean;
   menuId: string;
   triggerId: string;
   openWith: (focus: OpenFocus) => void;
   close: (options: { returnFocus: boolean }) => void;
}

/** What event handlers and effects share but rendering never reads. */
interface DropdownMenuInternals {
   trigger: HTMLElement | null;
   menu: HTMLDivElement | null;
   openFocus: OpenFocus;
   search: string;
   searchTimer: number;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);
const InternalsContext = createContext<RefObject<DropdownMenuInternals> | null>(null);
const RadioGroupContext = createContext<{
   value: string | undefined;
   onValueChange?: (value: string) => void;
} | null>(null);

interface DropdownMenuProps {
   open?: boolean;
   defaultOpen?: boolean;
   onOpenChange?: (open: boolean) => void;
   children?: ReactNode;
}

function DropdownMenu({
   open: openProp,
   defaultOpen = false,
   onOpenChange,
   children,
}: DropdownMenuProps) {
   // The hook's own onChange only fires in controlled mode; setOpen below
   // notifies uncontrolled consumers too.
   const [open, setOpenState] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
   });
   const triggerId = useId();
   const menuId = useId();
   const internalsRef = useRef<DropdownMenuInternals>({
      trigger: null,
      menu: null,
      openFocus: 'menu',
      search: '',
      searchTimer: 0,
   });

   // Both closes of one click land before React re-renders, so `open` cannot
   // tell the second apart: an item's onSelect that moves focus out closes
   // the menu from the focusin listener, then the click closes it again.
   const reportedRef = useRef<boolean | null>(null);

   const setOpen = useStableCallback((next: boolean) => {
      if (next === reportedRef.current) return;
      reportedRef.current = next;
      queueMicrotask(() => {
         reportedRef.current = null;
      });
      setOpenState(next);
      onOpenChange?.(next);
   });

   const openWith = useStableCallback((focus: OpenFocus) => {
      internalsRef.current.openFocus = focus;
      if (open) focusInitial(internalsRef.current);
      else setOpen(true);
   });

   const close = useStableCallback(({ returnFocus }: { returnFocus: boolean }) => {
      const { menu, trigger } = internalsRef.current;
      const focused = document.activeElement;
      // An item's handler that moved focus on purpose (into an editor, say)
      // keeps it there.
      const focusInMenu = focused === document.body || menu?.contains(focused);
      if (returnFocus && focusInMenu) trigger?.focus({ preventScroll: true });
      setOpen(false);
   });

   // Carpenter builds without the React Compiler; unmemoized, every part of
   // every menu re-renders whenever its owner does.
   const context = useMemo(
      () => ({ open, menuId, triggerId, openWith, close }),
      [open, menuId, triggerId, openWith, close]
   );

   return (
      <DropdownMenuContext value={context}>
         <InternalsContext value={internalsRef}>{children}</InternalsContext>
      </DropdownMenuContext>
   );
}

function DropdownMenuTrigger({
   asChild = false,
   ref,
   onClick,
   onKeyDown,
   onKeyUp,
   ...props
}: ComponentProps<'button'> & { asChild?: boolean }) {
   const menu = useDropdownMenu();
   const internalsRef = useInternals();
   const setTriggerNode = useCallback(
      (node: HTMLButtonElement | null) => {
         internalsRef.current.trigger = node;
         assignRef(ref, node);
      },
      [internalsRef, ref]
   );
   const Comp = asChild ? Slot : 'button';

   return (
      <Comp
         ref={setTriggerNode}
         id={menu.triggerId}
         type="button"
         aria-haspopup="menu"
         aria-expanded={menu.open}
         aria-controls={menu.open ? menu.menuId : undefined}
         data-slot="dropdown-menu-trigger"
         data-state={menu.open ? 'open' : 'closed'}
         onClick={(event: MouseEvent<HTMLButtonElement>) => {
            onClick?.(event);
            if (event.defaultPrevented) return;
            if (menu.open) menu.close({ returnFocus: true });
            // A click without a press, from assistive tech or a script, gets
            // the keyboard's treatment.
            else menu.openWith(event.detail === 0 ? 'first' : 'menu');
         }}
         onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
            onKeyDown?.(event);
            if (event.defaultPrevented) return;
            switch (event.key) {
               case 'Enter':
               case ' ': {
                  if (menu.open) menu.close({ returnFocus: true });
                  else menu.openWith('first');
                  break;
               }
               case 'ArrowDown': {
                  menu.openWith('first');
                  break;
               }
               case 'ArrowUp': {
                  menu.openWith('last');
                  break;
               }
               default: {
                  return;
               }
            }
            event.preventDefault();
         }}
         onKeyUp={(event: KeyboardEvent<HTMLButtonElement>) => {
            onKeyUp?.(event);
            // Firefox clicks a button on Space keyup even when the keydown
            // was handled, which would toggle the menu straight back.
            if (event.key === ' ') event.preventDefault();
         }}
         {...props}
      />
   );
}

function DropdownMenuContent({
   className,
   children,
   align = 'center',
   side = 'bottom',
   sideOffset = 4,
   ref,
   onKeyDown,
   onPointerMove,
   ...props
}: ComponentProps<'div'> & {
   align?: 'start' | 'center' | 'end';
   side?: 'top' | 'bottom';
   sideOffset?: number;
}) {
   const { open, menuId, close } = useDropdownMenu();
   const internalsRef = useInternals();
   const [mounted, setMounted] = useState(open);
   if (open && !mounted) setMounted(true);

   const setMenuNode = useCallback(
      (node: HTMLDivElement | null) => {
         internalsRef.current.menu = node;
         assignRef(ref, node);
      },
      [internalsRef, ref]
   );

   // A layout effect, not useStableCallback: that one updates after paint, so
   // the first placement would still see the previous render's props.
   const placementRef = useRef<PopupPlacement>({ align, side, sideOffset });
   useLayoutEffect(() => {
      placementRef.current = { align, side, sideOffset };
   });

   useLayoutEffect(() => {
      const internals = internalsRef.current;
      const { menu, trigger } = internals;
      if (!open || !menu || !trigger) return;

      function reposition() {
         if (!menu || !trigger) return;
         const visible = placePopup(menu, trigger, placementRef.current);
         if (!visible) close({ returnFocus: false });
      }
      function isOutside(target: EventTarget | null) {
         return target instanceof Node && !menu?.contains(target) && !trigger?.contains(target);
      }
      // A press or focus elsewhere closes the menu and leaves focus where the
      // person put it.
      function handlePointerDown(event: globalThis.PointerEvent) {
         if (isOutside(event.target)) close({ returnFocus: false });
      }
      function handleFocusIn(event: FocusEvent) {
         if (isOutside(event.target)) close({ returnFocus: false });
      }
      // Dialogs, drawers and sheets listen for Escape on the document; the
      // window sees it first, so the key closes only the menu.
      function handleKeyDown(event: globalThis.KeyboardEvent) {
         if (event.key !== 'Escape') return;
         event.preventDefault();
         event.stopPropagation();
         close({ returnFocus: true });
      }

      menu.setAttribute('aria-labelledby', trigger.id);
      menu.showPopover?.();
      reposition();
      focusInitial(internals);

      const stopFollowing = followAnchor(menu, reposition);
      window.addEventListener('keydown', handleKeyDown, { capture: true });
      document.addEventListener('pointerdown', handlePointerDown, { capture: true });
      document.addEventListener('focusin', handleFocusIn);
      return () => {
         stopFollowing();
         window.removeEventListener('keydown', handleKeyDown, { capture: true });
         document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
         document.removeEventListener('focusin', handleFocusIn);
         window.clearTimeout(internals.searchTimer);
         internals.search = '';
         internals.openFocus = 'menu';
         menu.hidePopover?.();
      };
   }, [open, close, internalsRef]);

   useLayoutEffect(() => {
      if (open || !mounted) return;
      return afterExit(internalsRef.current.menu, () => setMounted(false));
   }, [open, mounted, internalsRef]);

   function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      // Tab leaves from the trigger, so focus moves on from where the menu
      // was opened, as if it had never taken focus.
      if (event.key === 'Tab') {
         close({ returnFocus: true });
         return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const internals = internalsRef.current;
      const move = FOCUS_MOVES.get(event.key);
      // Space picks the focused item, unless it is part of a word being typed.
      const typing = event.key.length === 1 && !(event.key === ' ' && internals.search === '');
      if (typing) {
         pushSearch(internals, event.key);
         focusMatch(internals);
      } else if (event.key === 'Enter' || event.key === ' ') {
         const item = event.target instanceof Element ? event.target.closest(ITEM_SELECTOR) : null;
         if (item instanceof HTMLElement && item.dataset.disabled == null) item.click();
      } else if (move) {
         focusItem(internals, move);
      } else {
         return;
      }
      event.preventDefault();
   }

   if (!mounted) return null;
   return (
      <div
         ref={setMenuNode}
         id={menuId}
         role="menu"
         aria-orientation="vertical"
         // "manual": the component owns dismissal, the browser only supplies
         // the top layer.
         popover="manual"
         tabIndex={-1}
         data-slot="dropdown-menu-content"
         data-state={open ? 'open' : 'closed'}
         className={cn(
            'bg-popover text-popover-foreground fixed inset-auto z-50 m-0 min-w-[8rem] overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg border p-1 outline-none',
            // In place, the menu inherits text styles from wherever it is
            // used rather than from <body>.
            'font-normal text-start normal-case tracking-normal whitespace-normal',
            'origin-(--popup-transform-origin) scale-95 opacity-0 transition-[opacity,scale,display,overlay] transition-discrete duration-150 ease-out motion-reduce:transition-[opacity,display,overlay] data-[state=closed]:pointer-events-none',
            'data-[state=open]:scale-100 data-[state=open]:opacity-100 data-[state=open]:duration-200 starting:data-[state=open]:scale-95 starting:data-[state=open]:opacity-0',
            className
         )}
         onKeyDown={handleKeyDown}
         onPointerMove={event => {
            onPointerMove?.(event);
            // A finger moving over the menu is the menu's: a drawer or a
            // draggable row around it must not take it for a drag of its own.
            event.stopPropagation();
         }}
         {...props}
      >
         {children}
      </div>
   );
}

function DropdownMenuGroup(props: ComponentProps<'div'>) {
   return <div role="group" data-slot="dropdown-menu-group" {...props} />;
}

type ItemProps = Omit<ComponentProps<'div'>, 'onSelect'> & {
   asChild?: boolean;
   disabled?: boolean;
   /** Runs on a click or Enter; preventDefault() keeps the menu open. */
   onSelect?: (event: Event) => void;
};

function DropdownMenuItem({
   className,
   asChild = false,
   variant = 'default',
   ...props
}: ItemProps & { variant?: 'default' | 'destructive' }) {
   const itemProps = useItemProps(props);
   const Comp = asChild ? Slot : 'div';
   return (
      <Comp
         role="menuitem"
         data-slot="dropdown-menu-item"
         data-variant={variant}
         className={cn(
            "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none any-pointer-coarse:py-3 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            className
         )}
         {...itemProps}
      />
   );
}

function DropdownMenuRadioGroup({
   value,
   onValueChange,
   ...props
}: Omit<ComponentProps<'div'>, 'defaultValue'> & {
   value?: string;
   onValueChange?: (value: string) => void;
}) {
   const context = useMemo(() => ({ value, onValueChange }), [value, onValueChange]);
   return (
      <RadioGroupContext value={context}>
         <div role="group" data-slot="dropdown-menu-radio-group" {...props} />
      </RadioGroupContext>
   );
}

function DropdownMenuRadioItem({
   className,
   children,
   value,
   indicator = 'dot',
   onSelect,
   ...props
}: Omit<ItemProps, 'asChild'> & {
   value: string;
   /**
    * `dot`: a radio dot in the left gutter. `check`: a check on the right and
    * a tinted row for the chosen item, the way a Select shows its value.
    */
   indicator?: 'dot' | 'check';
}) {
   const group = use(RadioGroupContext);
   invariant(group, 'DropdownMenuRadioItem must be rendered inside a DropdownMenuRadioGroup');
   const checked = group.value === value;
   const itemProps = useItemProps({
      ...props,
      onSelect: event => {
         onSelect?.(event);
         group.onValueChange?.(value);
      },
   });
   return (
      <div
         role="menuitemradio"
         aria-checked={checked}
         data-slot="dropdown-menu-radio-item"
         data-state={checked ? 'checked' : 'unchecked'}
         className={cn(
            "focus:bg-accent focus:text-accent-foreground relative flex cursor-pointer items-center gap-2 rounded-sm py-1.5 text-sm outline-hidden select-none any-pointer-coarse:py-3 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            indicator === 'dot'
               ? 'pr-2 pl-8'
               : 'pr-8 pl-2 data-[state=checked]:bg-brand-50 data-[state=checked]:text-brand-700 data-[state=checked]:focus:bg-brand-100',
            className
         )}
         {...itemProps}
      >
         <span
            aria-hidden
            className={cn(
               'pointer-events-none absolute flex size-3.5 items-center justify-center',
               indicator === 'dot' ? 'left-2' : 'right-2'
            )}
         >
            {checked &&
               (indicator === 'dot' ? (
                  <FaIcon name="circle" className="size-2 fill-current" />
               ) : (
                  <FaIcon name="check" className="size-3.5" />
               ))}
         </span>
         {children}
      </div>
   );
}

function DropdownMenuLabel({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="dropdown-menu-label"
         className={cn('px-2 py-1.5 text-sm font-medium', className)}
         {...props}
      />
   );
}

function DropdownMenuSeparator({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         role="separator"
         aria-orientation="horizontal"
         data-slot="dropdown-menu-separator"
         className={cn('bg-border -mx-1 my-1 h-px', className)}
         {...props}
      />
   );
}

function DropdownMenuShortcut({ className, ...props }: ComponentProps<'span'>) {
   return (
      <span
         data-slot="dropdown-menu-shortcut"
         className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)}
         {...props}
      />
   );
}

const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"]';
const SEARCH_RESET_MS = 700;

type FocusMove = 'next' | 'previous' | 'first' | 'last';
const FOCUS_MOVES = new Map<string, FocusMove>([
   ['ArrowDown', 'next'],
   ['ArrowUp', 'previous'],
   ['Home', 'first'],
   ['PageUp', 'first'],
   ['End', 'last'],
   ['PageDown', 'last'],
]);

function useDropdownMenu() {
   const context = use(DropdownMenuContext);
   invariant(context, 'DropdownMenu parts must be rendered inside a DropdownMenu');
   return context;
}

function useInternals() {
   const internalsRef = use(InternalsContext);
   invariant(internalsRef, 'DropdownMenu parts must be rendered inside a DropdownMenu');
   return internalsRef;
}

/**
 * The behavior every kind of item shares. A disabled item stays focusable, as
 * the APG recommends for menus, so a screen reader still hears it is there.
 */
function useItemProps({
   disabled = false,
   onSelect,
   onClick,
   onPointerMove,
   onPointerLeave,
   ...props
}: Omit<ItemProps, 'asChild'>) {
   const { close } = useDropdownMenu();
   const internalsRef = useInternals();
   return {
      tabIndex: -1,
      'aria-disabled': disabled || undefined,
      'data-disabled': disabled ? '' : undefined,
      ...props,
      onClick(event: MouseEvent<HTMLDivElement>) {
         if (disabled) {
            // An item rendered as a link must not follow it.
            event.preventDefault();
            return;
         }
         onClick?.(event);
         if (event.defaultPrevented) return;
         const selectEvent = new Event('select', { cancelable: true });
         onSelect?.(selectEvent);
         if (!selectEvent.defaultPrevented) close({ returnFocus: true });
      },
      onPointerMove(event: PointerEvent<HTMLDivElement>) {
         onPointerMove?.(event);
         // A finger scrolling the menu should not drag the focus with it.
         if (event.pointerType === 'touch' || document.activeElement === event.currentTarget) {
            return;
         }
         event.currentTarget.focus({ preventScroll: true });
      },
      onPointerLeave(event: PointerEvent<HTMLDivElement>) {
         onPointerLeave?.(event);
         if (document.activeElement === event.currentTarget) {
            internalsRef.current.menu?.focus({ preventScroll: true });
         }
      },
   };
}

function menuItems(menu: HTMLElement | null) {
   return Array.from(menu?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
}

function focusElement(element: HTMLElement | null | undefined) {
   element?.focus({ preventScroll: true });
   element?.scrollIntoView?.({ block: 'nearest' });
}

function focusInitial({ menu, openFocus }: DropdownMenuInternals) {
   if (openFocus === 'menu') menu?.focus({ preventScroll: true });
   else focusElement(openFocus === 'first' ? menuItems(menu)[0] : menuItems(menu).at(-1));
}

/** Arrow keys wrap around the ends, as in the APG's menu examples. */
function focusItem({ menu }: DropdownMenuInternals, move: FocusMove) {
   const items = menuItems(menu);
   if (items.length === 0) return;
   const current = items.indexOf(document.activeElement as HTMLElement);
   const last = items.length - 1;
   const next = {
      first: 0,
      last,
      next: current === -1 || current === last ? 0 : current + 1,
      previous: current <= 0 ? last : current - 1,
   }[move];
   focusElement(items[next]);
}

function pushSearch(internals: DropdownMenuInternals, char: string) {
   window.clearTimeout(internals.searchTimer);
   internals.search += char.toLowerCase();
   internals.searchTimer = window.setTimeout(() => {
      internals.search = '';
   }, SEARCH_RESET_MS);
}

function focusMatch(internals: DropdownMenuInternals) {
   const items = menuItems(internals.menu);
   const { search } = internals;
   // The same letter again steps through the items that start with it.
   const cycling = Array.from(search).every(char => char === search[0]);
   const query = cycling ? search[0] : search;
   const current = items.indexOf(document.activeElement as HTMLElement);
   const from = cycling ? current + 1 : Math.max(current, 0);
   const match = [...items.slice(from), ...items.slice(0, from)].find(item =>
      (item.textContent ?? '').trim().toLowerCase().startsWith(query)
   );
   if (match) focusElement(match);
}

export {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuGroup,
   DropdownMenuItem,
   DropdownMenuLabel,
   DropdownMenuRadioGroup,
   DropdownMenuRadioItem,
   DropdownMenuSeparator,
   DropdownMenuShortcut,
   DropdownMenuTrigger,
};
